/**
 * The pure prefetch helper, kept out of `prefetcher.tsx` so that file exports a
 * component and nothing else (react-doctor/only-export-components — a module
 * mixing a component with helpers breaks Fast Refresh for the helper's callers).
 */
import type { useQueryClient } from '@tanstack/react-query'
import { createIntegrationConnectionsQueryOptions } from 'v2/pages/settings/integrations/connection-queries'
import type { IntegrationSourceGateway } from 'v2/shared/services/integration-source-gateway'

type PrefetchQueryClient = Pick<ReturnType<typeof useQueryClient>, 'prefetchQuery'>

interface PrefetchRollout {
  gateway: IntegrationSourceGateway
  isResolved: boolean
  useAciIntegrations: boolean
}

// Prefetch through the same source-aware options the integrations pages use, so
// the warmed cache entry is keyed by the resolved source and is actually reused.
// The rollout is a REQUIRED argument (prefetchQuery ignores `enabled`): warming
// this without it fired the legacy request for every authenticated user, ACI orgs
// included — a call the flag says that org must not make.
export const prefetchIntegrationConnections = (
  client: PrefetchQueryClient,
  rollout: PrefetchRollout,
) => {
  if (!rollout.isResolved) return undefined
  return client.prefetchQuery({
    ...createIntegrationConnectionsQueryOptions({
      gateway: rollout.gateway,
      isResolved: rollout.isResolved,
      rolloutEnabled: rollout.useAciIntegrations,
    }),
    meta: { silent: true },
  })
}
